import { z } from 'zod';
import { emailSchema } from './auth.js';

/**
 * Adult membership of a family. A family has one `owner` plus any number of
 * invited adults: `co_parent` (full management, like the owner) and
 * `trusted_adult` (can chat in rooms they're added to + view, but cannot manage
 * kids, settings, or invite others). Server is the authority on these roles.
 */
export const FAMILY_ROLES = ['owner', 'co_parent', 'trusted_adult'] as const;
export type FamilyRole = (typeof FAMILY_ROLES)[number];

/** Roles an existing adult can be invited as (never `owner`). */
export const INVITABLE_FAMILY_ROLES = ['co_parent', 'trusted_adult'] as const;

/** Roles allowed to manage the family (kids, settings, invites, rooms). */
export const FAMILY_MANAGE_ROLES: readonly FamilyRole[] = ['owner', 'co_parent'];
export function canManageFamily(role: FamilyRole): boolean {
  return FAMILY_MANAGE_ROLES.includes(role);
}

/** A managing adult creates an invite code for another adult. */
export const createFamilyInviteSchema = z.object({
  role: z.enum(INVITABLE_FAMILY_ROLES),
  /** Optional now (we show the code to copy); used for email delivery later. */
  email: emailSchema.optional(),
});
export type CreateFamilyInviteInput = z.infer<typeof createFamilyInviteSchema>;

/** Redeem a family invite code (existing, logged-in adult). */
export const joinFamilySchema = z.object({
  code: z.string().trim().min(8).max(64),
});
export type JoinFamilyInput = z.infer<typeof joinFamilySchema>;

/** Switch the family an adult is currently acting within (multi-family adults). */
export const switchFamilySchema = z.object({
  familyId: z.string().min(1).max(64),
});
export type SwitchFamilyInput = z.infer<typeof switchFamilySchema>;

/** Leave a family the adult belongs to (owners can't leave their own family). */
export const leaveFamilySchema = z.object({
  familyId: z.string().min(1).max(64),
});
export type LeaveFamilyInput = z.infer<typeof leaveFamilySchema>;

/** Owner hands off ownership to another adult already in the family. */
export const transferOwnershipSchema = z.object({
  toUserId: z.string().min(1).max(64),
});
export type TransferOwnershipInput = z.infer<typeof transferOwnershipSchema>;

/** Owner schedules the whole family for deletion; re-auth like account deletion. */
export const deleteFamilySchema = z.object({
  currentPassword: z.string().min(1).max(200),
});
export type DeleteFamilyInput = z.infer<typeof deleteFamilySchema>;

/** One family an adult belongs to, for the family switcher. */
export interface FamilyMembershipView {
  familyId: string;
  name: string;
  role: FamilyRole;
  /** True for the family the adult is currently acting within. */
  isActive: boolean;
}

export interface FamilyMemberView {
  userId: string;
  displayName: string;
  role: FamilyRole;
  /** True for the requesting adult's own row. */
  isYou: boolean;
}

export interface FamilyInviteView {
  id: string;
  role: FamilyRole;
  email: string | null;
  createdAt: string;
  expiresAt: string;
}

/** An invite awaiting THIS adult's response (matched by their email) — the
 *  invitee's view, as opposed to `FamilyInviteView` (the inviting family's view). */
export interface PendingInviteView {
  id: string;
  familyName: string;
  role: FamilyRole;
  createdAt: string;
  expiresAt: string;
}
